import { css } from '@emotion/css';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import {
  CodeInput,
  Container,
  Content,
  ErrorText,
  Headline,
  Text,
} from '@volumental/common';
import { type JSX, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cancelOtpSession, resendOTP, validateOTP } from '../../auth';
import { Header } from '../../components/accessible-components';
import { uiConfig } from '../../services/config';
import { useInputStore } from '../../store';

const resendCode = css`
  font-family: ${uiConfig.styles.fontFamily};
  font-style: normal;
  font-weight: ${uiConfig.styles.fontWeights.regular};
  font-size: 12px;
  line-height: 14px;
  white-space: pre-line;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  color: ${uiConfig.styles.colors.black};
  p {
    margin: 0.25rem 0;
  }
`;

const resendCodeButton = css`
  font-family: inherit;
  font-style: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;

  margin: 0 0.25rem;
  padding: 0;
  color: black;
  border: none;
  background: transparent;
  outline: none;
  text-decoration: underline;
  text-underline-offset: 0.2rem;
  &:hover {
    cursor: pointer;
  }
  &:focus {
    color: blue;
  }
`;

const CODE_DIGITS = 6;

interface ValidateAccountProps {
  onValidated: () => void;
  onRestart: () => void;
  onClose: () => void;
}
export function ValidateAccount({
  onValidated,
  onRestart,
  onClose,
}: ValidateAccountProps): JSX.Element {
  const { email, clear } = useInputStore((store) => store);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hideResendCode, setHideResendCode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  const onChange = (code?: string): void => {
    if (!code || code.length !== CODE_DIGITS) {
      setErrorMessage('');
      return;
    }
    setLoading(true);
    validateOTP(code)
      .then((res) => {
        setLoading(false);
        if (res.status === 'OK') {
          usageTracker.track(UsageTrackerEvent.VerifiedEmail);
          onValidated();
        } else if (res.status === 'EXPIRED_USER_INPUT_CODE') {
          setErrorMessage(t('enteredExpiredCode'));
        } else {
          setErrorMessage(t('enteredIncorrectCode'));
        }
      })
      .catch(() => {
        setLoading(false);
        setErrorMessage(t('enteredIncorrectCode'));
      });
  };

  const onBack = (): void => {
    void cancelOtpSession().then(() => {
      clear();
      onRestart();
    });
  };

  const onResendCode = (): void => {
    void resendOTP().then((res) => {
      if (res.status === 'OK') {
        setHideResendCode(true);
      }
    });
  };

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (hideResendCode) {
      timerRef.current = setTimeout(() => {
        setHideResendCode(false);
      }, 30 * 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [hideResendCode, setHideResendCode]);

  return (
    <Container>
      <Header back={onBack} close={onClose} />
      <Content>
        <Headline>{t('enterTheCode')}</Headline>
        <Text>
          {t('enterTheCodeSentToEmail')}
          <strong>{email}</strong>.
        </Text>
        <CodeInput
          digits={CODE_DIGITS}
          onlyNumbers
          onChange={onChange}
          error={errorMessage !== ''}
          loading={loading}
          ariaLabeledBy={t('ariaLabels.codeInputsLabel')}
          hiddenTitle={t('codeInputGroupTitle')}
          inputAriaLabel={t('ariaLabels.codeInput')}
          inputTitle={t('verificationInputTitle')}
        />
        {Boolean(errorMessage) && <ErrorText>{errorMessage}</ErrorText>}
        <div className={resendCode}>
          <p>{t('lookInYourSpam')}</p>
          {hideResendCode ? (
            <p>
              {t('newCodeSent')}
              <strong>{email}</strong>.
            </p>
          ) : (
            <p>
              {t('didntReceiveCode')}
              <button
                type="button"
                hidden={hideResendCode}
                className={resendCodeButton}
                onClick={onResendCode}
              >
                {t('button.resendCode')}
              </button>
            </p>
          )}
        </div>
      </Content>
    </Container>
  );
}
